import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, CardHeader, Button, Input } from '../../components/ui';
import { organizationAPI } from '../../api';
import { useToast } from '../../contexts/ToastContext';
import { copyToClipboard } from '../../utils/clipboard';
import AttioEmailAccessCard from './AttioEmailAccessCard';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Backend defaults (ATTIO_ATTRIBUTE_DEFAULTS in org_integration_config.py).
// A blank slug field means "use the default".
const ATTRIBUTE_DEFAULTS = {
  linkedin: 'linkedin',
  summary: 'description',
  zamanat_lead_id: 'zamanat_lead_id',
  platform_source: 'platform_source',
};

const ATTRIBUTE_LABELS = {
  linkedin: 'LinkedIn attribute slug',
  summary: 'Summary attribute slug',
  zamanat_lead_id: 'Zamanat Lead ID attribute slug',
  platform_source: 'Platform Source attribute slug',
};

const SETUP_STEP_LABELS = {
  workspace: 'Check the key and workspace',
  people_object: 'Read the people object',
  attributes: 'Create custom attributes',
  delete_old_webhook: 'Delete the old webhook',
  create_webhook: 'Create the webhook',
  store_webhook: 'Store the webhook secret',
};

// Write-only fields: sent only when the admin typed a value
const SECRET_FIELDS = ['api_key', 'webhook_secret'];

const STATUS_CLASSES = {
  ok: 'text-green-600 dark:text-green-400',
  failed: 'text-red-600 dark:text-red-400',
  skipped: 'text-gray-500 dark:text-gray-400',
};

const emptyAttributes = () => ({
  linkedin: '',
  summary: '',
  zamanat_lead_id: '',
  platform_source: '',
});

const defaultConfig = {
  enabled: false,
  sync_enabled: false,
  api_key: false,
  webhook_secret: false,
  webhook_id: null,
  workspace_slug: null,
  default_phone_region: null,
  attributes: { ...ATTRIBUTE_DEFAULTS },
};

// The backend raises HTTPException, so the message sits under detail.error.
const errorMessage = (err, fallback) =>
  err?.response?.data?.detail?.error?.message ||
  err?.response?.data?.error?.message ||
  err?.response?.data?.message ||
  fallback;

// The GET response fills in defaults. Show a default as a blank field, so the
// placeholder carries it and a save does not pin it as a custom value.
const attributesToForm = (attributes = {}) => {
  const form = emptyAttributes();
  Object.keys(ATTRIBUTE_DEFAULTS).forEach((key) => {
    const value = attributes[key];
    form[key] = value && value !== ATTRIBUTE_DEFAULTS[key] ? value : '';
  });
  return form;
};

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none ${
      checked ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const AttioConfigTab = ({ orgId }) => {
  const toast = useToast();
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [settingUp, setSettingUp] = useState(false);
  const [setupResult, setSetupResult] = useState(null);

  // Form state — secrets are write-only inputs
  const [form, setForm] = useState({
    enabled: false,
    api_key: '',
    webhook_secret: '',
    sync_enabled: false,
    default_phone_region: '',
    attributes: emptyAttributes(),
  });

  const setTextField = (field) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [field]: value }));
  };

  // Keyed by organization UUID, so a slug rename does not break it
  const webhookUrl = orgId ? `${API_BASE_URL}/api/v1/webhooks/attio/${orgId}` : '';

  const applyConfig = useCallback((data) => {
    setConfig(data);
    setForm((prev) => ({
      ...prev,
      enabled: data.enabled,
      sync_enabled: data.sync_enabled,
      default_phone_region: data.default_phone_region || '',
      attributes: attributesToForm(data.attributes),
    }));
  }, []);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await organizationAPI.getAttioConfig(orgId);
      applyConfig(data);
    } catch {
      toast.error('Failed to load Attio configuration');
    } finally {
      setLoading(false);
    }
  }, [orgId, toast, applyConfig]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        sync_enabled: form.sync_enabled,
        default_phone_region: form.default_phone_region.trim(),
        // Blank resets a slug to its default on the backend
        attributes: form.attributes,
      };
      // Only send secrets if the user typed something
      SECRET_FIELDS.forEach((field) => {
        if (form[field]) payload[field] = form[field];
      });

      const updated = await organizationAPI.updateAttioConfig(orgId, payload);
      applyConfig(updated);
      setForm((prev) => ({ ...prev, api_key: '', webhook_secret: '' }));
      toast.success('Attio configuration saved');
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to save Attio configuration'));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(await organizationAPI.testAttioConnection(orgId));
    } catch (err) {
      setTestResult({ error: errorMessage(err, 'Connection test failed') });
    } finally {
      setTesting(false);
    }
  };

  const handleSetup = async () => {
    setSettingUp(true);
    setSetupResult(null);
    try {
      const result = await organizationAPI.setupAttio(orgId);
      setSetupResult(result);
      if (result.config) {
        // Keep unsaved form edits for fields setup does not touch
        setConfig(result.config);
      }
      if (result.ok) {
        toast.success('Attio setup finished');
      } else {
        toast.error('Attio setup did not finish. See the step results.');
      }
    } catch (err) {
      setSetupResult({ error: errorMessage(err, 'Attio setup failed') });
    } finally {
      setSettingUp(false);
    }
  };

  const handleCopyWebhook = async () => {
    const ok = await copyToClipboard(webhookUrl);
    if (ok) {
      toast.success('Webhook URL copied');
    } else {
      toast.error('Failed to copy');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardBody>
          <p className="text-center text-gray-500 dark:text-gray-400 py-4">Loading…</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Attio</h2>
      </CardHeader>
      <CardBody className="space-y-6">

        {/* Email-capture note */}
        <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
          <p className="text-xs text-amber-800 dark:text-amber-300">
            Attio sends no emails to Zamanat by itself. To save lead emails from Attio, connect
            Attio email access below. Close email capture also works, if Close is enabled.
          </p>
        </div>

        {/* Enable integration toggle */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white">Enable integration</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              When disabled, Attio webhook requests for this organization are dropped. This does not change the Close tab.
            </p>
          </div>
          <Toggle checked={form.enabled} onChange={() => setForm((f) => ({ ...f, enabled: !f.enabled }))} />
        </div>

        {form.enabled && (
          <>
            <hr className="border-gray-200 dark:border-gray-700" />

            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Attio API Key
                {config.api_key && (
                  <span className="ml-2 text-xs text-green-600 dark:text-green-400">(configured)</span>
                )}
              </label>
              <Input
                type="password"
                placeholder={config.api_key ? 'Leave blank to keep existing key' : 'Enter API key'}
                value={form.api_key}
                onChange={setTextField('api_key')}
                autoComplete="new-password"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Create the key in Attio under Workspace settings, Developers. It needs these scopes:
                <code className="mx-1 text-xs bg-gray-100 dark:bg-gray-800 rounded px-1">record_permission:read-write</code>
                <code className="mx-1 text-xs bg-gray-100 dark:bg-gray-800 rounded px-1">object_configuration:read-write</code>
                <code className="mx-1 text-xs bg-gray-100 dark:bg-gray-800 rounded px-1">webhook:read-write</code>
              </p>
            </div>

            {/* Test connection and setup — both use the saved key */}
            <div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" onClick={handleTest} disabled={testing || !config.api_key}>
                  {testing ? 'Testing…' : 'Test connection'}
                </Button>
                <Button size="sm" onClick={handleSetup} disabled={settingUp || !config.api_key}>
                  {settingUp ? 'Setting up…' : 'Set up Attio'}
                </Button>
              </div>
              {!config.api_key && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  Save an API key first. Test and setup use the saved key.
                </p>
              )}

              {testResult && (
                <div className="mt-3 text-sm">
                  {testResult.error ? (
                    <p className="text-red-600 dark:text-red-400">{testResult.error}</p>
                  ) : !testResult.active ? (
                    <p className="text-red-600 dark:text-red-400">
                      Attio reports this key as inactive. Create a new key in Attio and save it.
                    </p>
                  ) : (
                    <>
                      <p className="text-green-600 dark:text-green-400">
                        Connected to workspace {testResult.workspace_name || testResult.workspace_slug}.
                      </p>
                      {testResult.missing_scopes?.length > 0 && (
                        <p className="text-amber-600 dark:text-amber-400">
                          Missing scopes: {testResult.missing_scopes.join(', ')}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              {setupResult && (
                <div className="mt-3 text-sm">
                  {setupResult.error ? (
                    <p className="text-red-600 dark:text-red-400">{setupResult.error}</p>
                  ) : (
                    <ul className="space-y-1">
                      {(setupResult.steps || []).map((s) => (
                        <li key={s.step}>
                          <span className={`font-medium ${STATUS_CLASSES[s.status] || ''}`}>
                            {s.status}
                          </span>{' '}
                          <span className="text-gray-700 dark:text-gray-300">
                            {SETUP_STEP_LABELS[s.step] || s.step}:
                          </span>{' '}
                          <span className="text-gray-500 dark:text-gray-400">{s.message}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <hr className="border-gray-200 dark:border-gray-700" />

            {/* Email access through the Attio MCP server */}
            {config.enabled ? (
              <AttioEmailAccessCard orgId={orgId} />
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Save the configuration with the integration enabled. Then you can connect Attio email access.
              </p>
            )}

            <hr className="border-gray-200 dark:border-gray-700" />

            {/* Webhook URL */}
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                Inbound Webhook URL
                {config.webhook_id && (
                  <span className="ml-2 text-xs text-green-600 dark:text-green-400">(webhook created)</span>
                )}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Set up Attio creates the webhook for you. Use this URL only to create it by hand.
              </p>
              <div className="flex gap-2 items-center">
                <code className="flex-1 min-w-0 text-xs bg-gray-100 dark:bg-gray-800 rounded px-3 py-2 break-all select-all">
                  {webhookUrl}
                </code>
                <Button size="sm" variant="secondary" onClick={handleCopyWebhook} className="shrink-0">
                  Copy
                </Button>
              </div>
            </div>

            {/* Webhook secret — manual setup only */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Webhook Secret
                {config.webhook_secret && (
                  <span className="ml-2 text-xs text-green-600 dark:text-green-400">(configured)</span>
                )}
              </label>
              <Input
                type="password"
                placeholder={config.webhook_secret ? 'Leave blank to keep existing secret' : 'Only for a webhook created by hand'}
                value={form.webhook_secret}
                onChange={setTextField('webhook_secret')}
                autoComplete="new-password"
              />
              {!config.webhook_secret && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  No secret is stored. Webhook requests are rejected (401) until you run Set up Attio or paste a secret.
                </p>
              )}
            </div>

            {/* Outbound sync toggle */}
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Outbound sync</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Push manually created and edited leads to Attio.
                </p>
              </div>
              <Toggle checked={form.sync_enabled} onChange={() => setForm((f) => ({ ...f, sync_enabled: !f.sync_enabled }))} />
            </div>

            {/* Default phone region */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Default phone region
              </label>
              <Input
                placeholder="PK"
                maxLength={2}
                value={form.default_phone_region}
                onChange={(e) => setForm((f) => ({ ...f, default_phone_region: e.target.value.toUpperCase() }))}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Two-letter country code. Used to turn a local phone number into international format before the push.
              </p>
            </div>

            <hr className="border-gray-200 dark:border-gray-700" />

            {/* Attribute slugs */}
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Attribute slugs</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                Attio people attributes that Zamanat writes. Leave blank to use the default shown.
              </p>
              <div className="space-y-3">
                {Object.keys(ATTRIBUTE_LABELS).map((key) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {ATTRIBUTE_LABELS[key]}
                    </label>
                    <Input
                      placeholder={ATTRIBUTE_DEFAULTS[key]}
                      value={form.attributes[key]}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          attributes: { ...f.attributes, [key]: e.target.value },
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Configuration'}
          </Button>
        </div>

      </CardBody>
    </Card>
  );
};

export default AttioConfigTab;
